/**
 * The invitation landing page, routed at `/invite?token=…` as `<page-invite>`.
 *
 * Implements `SC-F104`'s recipient half and `SC-F105` (expiry and revocation as
 * the invitee experiences them). Stories: `SC-S104`, `SC-S105`.
 *
 * **Deliberately unguarded, and the lookup is anonymous.** The link arrives in
 * a mailbox and the recipient may have no account at all, so `router.ts` runs
 * no guard here and `lookupInvite` sends no bearer token (`06` §3.3). The page
 * resolves the invitation *first* and only then asks anyone to sign in, sending
 * them to `/sign-in?next=<this exact URL>` itself — guarding the route instead
 * would bounce every new invitee to a sign-in form with no explanation of what
 * they had been invited to, and attaching a stale stored token to the lookup
 * would produce a `401` that has nothing to do with the invitation.
 *
 * **One failure message, on purpose.** An unknown token, an expired invitation,
 * a revoked one and one that was already accepted all answer the identical
 * `404 not_found` (`SC-S105`). That uniformity *is* the anti-enumeration
 * control: this route is reachable by anyone with a URL bar, and a page that
 * distinguished the four would turn it into an oracle for which organizations
 * exist and who has been invited to them. So the terminal state names no
 * organization, offers exactly one way forward, and says out loud that the
 * ambiguity is deliberate rather than letting it read as a broken page. The
 * *authenticated* pending-invitation list is where the precise states live,
 * because a maintainer already knows the organization exists.
 *
 * A transport failure is a different thing and is kept separate. A dropped
 * connection or a `429` from the lookup brake says nothing about the token, and
 * rendering the terminal verdict over one is worse than unhelpful: the invitee
 * acts on it and asks for a replacement, which *replaces the pending row* and
 * kills the link they were holding — the page's own false verdict destroying a
 * working invitation. That branch retries instead.
 *
 * **The four states.** *Loading* is the lookup; *empty* is the terminal
 * unusable-invitation state; *error* is the retryable lookup failure, rendering
 * the `apierr` code, its message and the `request_id`; *populated* is the
 * invitation itself, in one of three shapes — signed out, signed in as the
 * invited address, or signed in as a different account.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "../components/index.js";
import { toast } from "../components/index.js";

import { currentUser, signOut } from "../api/auth.js";
import { pb } from "../api/client.js";
import { type ApiError, parseError, supportReference } from "../api/errors.js";
import { acceptInvite, lookupInvite, type InviteLookup } from "../api/invites.js";
import { ROLE_LABELS } from "../api/orgs.js";
import { hrefFor, navigate } from "../nav.js";

@customElement("page-invite")
export class PageInvite extends LitElement {
  @state() private loading = true;
  @state() private invite: InviteLookup | null = null;

  /** True once the invitation is known to be unusable — the uniform `404`, or
   * a link with no token at all. Terminal: there is nothing to retry. */
  @state() private unusable = false;

  /** A retryable lookup failure. Says nothing about the token. */
  @state() private loadError: ApiError | null = null;

  @state() private accepting = false;
  /** Set when accept answered `409 conflict`: the membership already exists. */
  @state() private alreadyMember = false;
  /** Set when the signed-in reader chose to accept as themselves despite the
   * address mismatch, which collapses the warning back to the normal view. */
  @state() private mismatchAcknowledged = false;

  /**
   * The token from the query string, read once. The router matches on pathname
   * only, so a page reads `location.search` for itself.
   */
  private readonly token = new URLSearchParams(location.search).get("token") ?? "";

  static override styles = css`
    :host {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding-top: clamp(var(--bino-space-lg), 8vh, 6rem);
    }
    /* The surface itself is bn-card; this only sizes it. */
    .card {
      width: 100%;
      max-width: 29rem;
      font-family: var(--bino-font-sans);
    }
    h1 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
      letter-spacing: -0.01em;
    }
    p {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      line-height: 1.55;
      color: var(--bino-text-secondary);
    }
    .fine {
      font-size: var(--bino-font-size-sm);
    }
    .org {
      color: var(--bino-text);
      font-weight: 600;
    }
    .addr {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text);
      word-break: break-all;
    }
    .role {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: var(--bino-space-xs) var(--bino-space-sm);
      margin-bottom: var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .warn {
      margin: 0 0 var(--bino-space-md);
      padding: var(--bino-space-sm) var(--bino-space-md);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
      font-size: var(--bino-font-size-sm);
      line-height: 1.55;
    }
    .code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      padding: 0 var(--bino-space-xs);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
      color: var(--bino-text);
    }
    .ref {
      margin: 0;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      /* One click selects the whole reference, which is what a reader pasting
         it into a support ticket is trying to do. */
      user-select: all;
    }
    .actions {
      margin-top: var(--bino-space-lg);
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
    }
    .foot {
      margin: var(--bino-space-md) 0 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      text-align: center;
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    if (this.token === "") {
      // A link with no token cannot be looked up, and it is the same dead end
      // as a token the server refuses. Same state, same copy — a separate
      // "malformed link" message would be one more thing to distinguish.
      this.unusable = true;
      this.loading = false;
      return;
    }
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.loadError = null;
    try {
      this.invite = await lookupInvite(this.token);
      this.unusable = false;
    } catch (err) {
      const e = parseError(err);
      // `not_found` is the only verdict about the invitation itself, and it
      // covers unknown, expired, revoked and already-accepted alike. Everything
      // else is a failure to *ask*, which must not be reported as an answer.
      if (e.code === "not_found") this.unusable = true;
      else this.loadError = e;
    } finally {
      this.loading = false;
    }
  }

  /** The sign-in detour, carrying this exact invitation URL as the return path
   * so the flow — which may be a first-ever sign-up — comes back here. */
  private signInHref(): string {
    return "/sign-in?next=" + encodeURIComponent(location.pathname + location.search);
  }

  private switchAccount() {
    // Clear the wrong session first, or the sign-in page sees a valid token and
    // bounces straight back here as the same mismatched reader.
    signOut();
    navigate(this.signInHref());
  }

  private async accept() {
    if (this.accepting) return;
    this.accepting = true;
    try {
      const { org } = await acceptInvite(this.token);
      toast(`You joined ${this.invite?.org.name ?? "the organization"}.`, "success");
      // `replace`: the token is spent, so the invitation URL must not sit in
      // the back stack waiting to render its own terminal state.
      navigate(hrefFor("orgs", org), { replace: true });
      // No `accepting = false` and no `finally`: the element is being torn
      // down, and setting state on it only schedules a render nobody sees.
      return;
    } catch (err) {
      const e = parseError(err);
      if (e.code === "not_found") {
        // The invitation died between the lookup and the accept — revoked while
        // this page was open, or it aged out. Swap in the terminal state rather
        // than toasting over a button that can no longer work.
        this.unusable = true;
        this.invite = null;
      } else if (e.code === "conflict") {
        this.alreadyMember = true;
      } else if (e.status === 401) {
        navigate(this.signInHref(), { replace: true });
      } else {
        toast(e.message, e.code === "rate_limited" ? "warning" : "error");
      }
      this.accepting = false;
    }
  }

  /** The address this invitation was sent to, compared case-insensitively:
   * `05` §3.3 stores `org_invites.email` lowercased, while the signed-in
   * account's address is whatever the reader typed. */
  private isMismatch(invite: InviteLookup): boolean {
    const me = currentUser()?.email ?? "";
    if (me === "") return false;
    return me.trim().toLowerCase() !== invite.email.trim().toLowerCase();
  }

  private formatExpiry(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  }

  override render() {
    if (this.loading) {
      return html`<bn-spinner block size="28" label="Checking this invitation"></bn-spinner>`;
    }
    if (this.unusable) return this.renderUnusable();
    if (this.loadError) return this.renderLoadError(this.loadError);
    if (!this.invite) return this.renderUnusable();
    if (this.alreadyMember) return this.renderAlreadyMember(this.invite);
    if (this.isMismatch(this.invite) && !this.mismatchAcknowledged) {
      return this.renderMismatch(this.invite);
    }
    return this.renderInvite(this.invite);
  }

  /**
   * The terminal state. It names no organization, and it carries **no button**:
   * `SC-S105` requires exactly one way forward — ask whoever invited you for a
   * new invitation — and every remedy this page could offer instead ("resend",
   * "retry", "contact support") would either be a second action or a lie about
   * what this page can do. Navigation is the app shell's job and its header is
   * still there.
   */
  private renderUnusable() {
    return html`<bn-card class="card">
      <bn-empty-state
        icon="⊘"
        heading="This invitation link cannot be used"
        message="Ask the person who invited you to send a new invitation."
      ></bn-empty-state>
      <p class="fine">
        We cannot tell you whether the link expired, was withdrawn, was already accepted or never
        existed — the answer is deliberately the same for all four, so that this page cannot be used
        to discover which organizations exist or who was invited to them.
      </p>
    </bn-card>`;
  }

  /**
   * The retryable counterpart. It makes no claim about the invitation, and its
   * primary action re-runs the lookup rather than sending the reader away to
   * ask for a replacement that would destroy the link they are holding.
   */
  private renderLoadError(err: ApiError) {
    const ref = supportReference(err);
    return html`<bn-card class="card">
      <h1>We could not check this invitation</h1>
      <p>${err.message} <span class="code">${err.code}</span></p>
      <p class="fine">
        Your link is unaffected — this was a failure to reach Sluice Cloud, not a verdict on the
        invitation. Try again in a moment.
      </p>
      ${ref ? html`<p class="ref">${ref}</p>` : nothing}
      <div class="actions">
        <bn-button variant="primary" block @click=${() => void this.load()}>Try again</bn-button>
      </div>
    </bn-card>`;
  }

  private renderAlreadyMember(invite: InviteLookup) {
    return html`<bn-card class="card">
      <h1>You are already in ${invite.org.name}</h1>
      <p>
        This invitation has nothing left to grant — your membership of
        <span class="org">${invite.org.name}</span> already exists. Your current role is whatever it
        was before the invitation was sent, which may differ from the role it offered.
      </p>
      <div class="actions">
        <bn-button
          variant="primary"
          block
          @click=${() => navigate(hrefFor("orgs", invite.org.id), { replace: true })}
        >
          Open ${invite.org.name}
        </bn-button>
      </div>
    </bn-card>`;
  }

  /**
   * Signed in as somebody else. This is a *warning*, not a refusal, because the
   * server does not refuse it: `SC-S104` specifies that acceptance binds to the
   * authenticated account even when the addresses differ, and records both in
   * the trail. So the page states exactly what will happen and lets the reader
   * choose, rather than blocking an act the API permits.
   */
  private renderMismatch(invite: InviteLookup) {
    const me = currentUser()?.email ?? "";
    return html`<bn-card class="card">
      <h1>Join ${invite.org.name} as ${me}?</h1>
      <p class="warn">
        This invitation was sent to <span class="addr">${invite.email}</span>, and you are signed in
        as <span class="addr">${me}</span>. Accepting is still allowed — the membership is created
        for the account you are signed in as, and both addresses are recorded in the organization's
        activity trail.
      </p>
      <p class="fine">
        If <span class="addr">${invite.email}</span> is the account you meant to use, sign out and
        come back through this link; nothing is spent until you accept.
      </p>
      <div class="actions">
        <bn-button
          variant="secondary"
          block
          @click=${() => {
            this.mismatchAcknowledged = true;
          }}
        >
          Continue as ${me}
        </bn-button>
        <bn-button variant="primary" block @click=${() => this.switchAccount()}>
          Sign out and use ${invite.email}
        </bn-button>
      </div>
    </bn-card>`;
  }

  private renderInvite(invite: InviteLookup) {
    const authed = pb.authStore.isValid;
    const me = currentUser()?.email ?? "";
    const expires = this.formatExpiry(invite.expires);
    const role = ROLE_LABELS[invite.role];
    return html`<bn-card class="card">
      <h1>Join ${invite.org.name}</h1>
      <p>
        You have been invited to <span class="org">${invite.org.name}</span> on Sluice Cloud, an
        organization that operates Sluice instances.
      </p>
      <div class="role">
        <span>Your role will be</span>
        <bn-badge variant="info">${role?.label ?? invite.role}</bn-badge>
      </div>
      <p class="fine">${role?.hint ?? ""}</p>
      <p class="fine">
        Sent to <span class="addr">${invite.email}</span>${expires
          ? html` · valid until ${expires}`
          : nothing}
      </p>

      <div class="actions">
        ${authed
          ? html`<bn-button
              variant="primary"
              block
              ?loading=${this.accepting}
              @click=${() => void this.accept()}
            >
              Accept invitation
            </bn-button>`
          : html`<bn-button variant="primary" block @click=${() => navigate(this.signInHref())}>
              Sign in to accept
            </bn-button>`}
      </div>

      ${authed
        ? html`<p class="foot">Signed in as <span class="addr">${me}</span></p>`
        : html`<p class="foot">
            No account yet? Signing in with your email address creates one — there is no separate
            sign-up.
          </p>`}
    </bn-card>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-invite": PageInvite;
  }
}
